import { useMutation, useQueryClient } from 'react-query'

import { attachmentRepository } from '@webapp/core/data-access/repositories/AttachmentRepository'
import { type RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import {
  startReconciliationSession,
  type StartReconciliationSessionResult,
} from '@webapp/modules/accounts-payable/business-logic/use-cases/StartReconciliationSession'
import { type StatementAttachmentType } from '@webapp/modules/accounts-payable/entities/enums/StatementAttachmentTypeEnum'
import { creditCardAccountQueries } from '@webapp/modules/accounts-payable/query-configs/creditCardAccountQueries'
import { CREDIT_CARD_ITEM_UNMATCHED_BASE_KEY } from '@webapp/modules/accounts-payable/query-configs/creditCardItemQueries'
import { STATEMENT_ITEM_UNMATCHED_BASE_KEY } from '@webapp/modules/accounts-payable/query-configs/statementItemQueries'

const readFileContent = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '')
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })

interface StartReconciliationSessionVariables {
  creditcardId: number
  existingSessionId: Nullable<number>
  /** YYYY-MM-DD — required in full mode */
  statementDate: Nullable<string>
  balance?: string
  attachmentFile: Nullable<File>
  attachmentType: Nullable<StatementAttachmentType>
}

export const useMutateStartReconciliationSession = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation<
    StartReconciliationSessionResult,
    RepoError,
    StartReconciliationSessionVariables
  >({
    // The statement upload happens inside the command flow; the use case
    // orchestrates session create-or-reuse + conditional statement (BHV-028).
    mutationFn: async ({
      creditcardId,
      existingSessionId,
      statementDate,
      balance,
      attachmentFile,
      attachmentType,
    }) => {
      const attachment = attachmentFile
        ? await attachmentRepository.upload({
            name: attachmentFile.name,
            type: attachmentFile.type,
            file: await readFileContent(attachmentFile),
          })
        : null

      return startReconciliationSession({
        creditcardId,
        existingSessionId,
        statementDate,
        balance,
        attachmentId: attachment?.uuid ?? null,
        attachmentType: attachment ? attachmentType : null,
      })
    },
    onSuccess: (_, { creditcardId }) => {
      queryClient.invalidateQueries({
        queryKey: creditCardAccountQueries.detail(creditcardId).queryKey,
      })
      queryClient.invalidateQueries({
        queryKey: STATEMENT_ITEM_UNMATCHED_BASE_KEY,
      })
      queryClient.invalidateQueries({
        queryKey: CREDIT_CARD_ITEM_UNMATCHED_BASE_KEY,
      })
    },
  })

  return {
    startSession: mutation.mutateAsync,
    isStarting: mutation.isLoading,
    error: mutation.error,
  }
}
